import { controlToastState } from "risa-oasis-ui_v2";
import { CmmDocType } from "../../enums/cmmDocType";
import { getAllDocumentsForOrder } from "../bigQuery/nycbsPharmaOrders";

export interface CmmDocument {
  identifier: string;
  document_type: string;
  file_path: string;
  created_at: string;
  document_name?: string;
}

/**
 * Helper function to safely get timestamp from date string
 * @param dateString - The date string to parse
 * @returns Timestamp in milliseconds or 0 if invalid
 */
const getValidTimestamp = (dateString?: string): number => {
  if (!dateString || dateString.trim() === "") {
    return 0;
  }

  const timestamp = new Date(dateString).getTime();
  return isNaN(timestamp) ? 0 : timestamp;
};

/**
 * Fetches all documents for a given order identifier
 * @param orderId - The order identifier
 * @returns Array of documents or empty array if no documents found
 * @throws Error if the API request fails
 */
export const fetchDocumentsForOrder = async (
  orderId: string,
): Promise<CmmDocument[]> => {
  if (!orderId) {
    controlToastState("orderId-required");
    return [];
  }

  try {
    const results = await getAllDocumentsForOrder(orderId);
    return results?.["details"] || [];
  } catch (error) {
    console.error("Error fetching documents for order:", error);
    controlToastState("error-fetching-documents");
    return [];
  }
};

/**
 * Gets the latest document of a specific type and returns its file path
 * @param orderId - The order identifier
 * @param docType - The document type to fetch
 * @returns File path or null if document not found
 * @throws Error if the API request fails
 */
export const getLatestDocFilePath = async (
  orderId: string,
  docType: CmmDocType,
): Promise<string | null> => {
  if (!orderId) {
    controlToastState("orderId-required");
  }

  if (!docType) {
    controlToastState("docType-required");
  }

  try {
    const documents = await fetchDocumentsForOrder(orderId);

    // Filter by document type and ensure valid created_at dates
    const filteredDocs = documents.filter((doc) => {
      if (doc.document_type !== docType) {
        return false;
      }

      // Filter out documents without valid dates
      const timestamp = getValidTimestamp(doc.created_at);
      return timestamp > 0;
    });

    if (filteredDocs && filteredDocs.length > 0) {
      // Sort by created_at in descending order (latest first)
      const latestDoc = filteredDocs.sort(
        (a, b) =>
          getValidTimestamp(b.created_at) - getValidTimestamp(a.created_at),
      )[0];

      return latestDoc?.file_path ?? null;
    }

    return null;
  } catch (error) {
    console.error("Error fetching document file path:", error);
    controlToastState("error-fetching-document-file-path");
    return null;
  }
};

/**
 * Gets the CMM screenshot file path for a specific order
 * @param orderId - The order identifier
 * @returns File path or null if document not found
 * @throws Error if the API request fails or parameters are invalid
 */
export const getCmmScreenshotFilePath = async (
  orderId: string,
): Promise<string | null> => {
  return await getLatestDocFilePath(orderId, CmmDocType.CMM_SCREENSHOT);
};
